import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Dialog } from '@mui/material';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import ToggleOffOutlinedIcon from '@mui/icons-material/ToggleOffOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from 'use-toast-mui';

import { getMemberGroups } from '../../../api/member-group';
import { updateBranchPointSetting } from '../../../api/member-point';
import { PAYMENT_METHODS, POINT_EARN_TYPE, POINT_EXPIRE_MODE, POINT_EXPIRE_SOURCE, POINT_EXPIRE_SOURCE_LABELS, POINT_PERIOD_UNIT } from '../../../constants/point';

/**
 * 포인트 설정 모달 (확정 디자인: 포인트 설정 (standalone).html)
 * - 좌측: 적립/사용/회원 그룹별 탭 + 기능 사용 마스터 토글
 * - 적립 탭: 기본 적립(%|P), 출석 적립(토글+정액), 결제 수단별 적립 그룹(자유 구성)
 * - 사용 탭: 최소 사용 포인트, 1회 최대 사용(%|원, 빈 값=무제한)
 * - 회원 그룹별 탭: 그룹마다 수단별 적립·출석·최소/최대 사용·앱 사용 (전부 필수, 추가 시 지점 설정값을 채운다)
 * - 하단: dirty 상태 표시 + 취소/저장
 */

const TEAL = '#12B5A5';
const TEAL_DARK = '#0E9487';

const numOf = value => {
  const n = parseFloat(String(value ?? '').replace(/[^0-9.]/g, ''));
  return Number.isNaN(n) ? 0 : n;
};
const strOf = value => (value === null || value === undefined ? '' : String(value));

/**
 * 적립률 입력 정제 — %(PERCENT)는 소수점 둘째 자리까지, 정액(P)은 정수만 받는다.
 * 백엔드 EARN_VALUE 는 DECIMAL(12,2) 이고 셋째 자리 이하는 400 으로 거부하므로 입력 단계에서 막는다.
 */
const sanitizeRate = (value, unit) => {
  const raw = String(value ?? '');
  if (unit !== POINT_EARN_TYPE.PERCENT) return raw.replace(/[^0-9]/g, '');
  const [head, ...rest] = raw.replace(/[^0-9.]/g, '').split('.');
  return rest.length ? `${head}.${rest.join('').slice(0, 2)}` : head;
};

// 서버 응답 → 편집 상태
const toEditState = setting => ({
  on: setting?.enabledYn === 'Y',
  base: strOf(setting?.earnValue ?? ''),
  baseUnit: setting?.earnType || POINT_EARN_TYPE.PERCENT,
  attendOn: setting?.attendanceEnabledYn === 'Y',
  attend: strOf(setting?.attendancePoint ?? ''),
  // 유형별 유효기간 규칙 — 서버가 유형 3개를 전부 내려주지만, 빠져도 OFF 로 채운다
  expireRules: Object.values(POINT_EXPIRE_SOURCE).map(source => {
    const rule = (setting?.expireRules || []).find(r => r.source === source);
    return {
      source,
      on: rule?.enabledYn === 'Y',
      mode: rule?.mode || POINT_EXPIRE_MODE.PRODUCT,
      value: strOf(rule?.periodValue ?? ''),
      unit: rule?.periodUnit || POINT_PERIOD_UNIT.MONTH,
    };
  }),
  // 소멸 예정 기준 — 만료일이 오늘+N일 이내면 "소멸 예정"(회원상세 요약·소멸 예정 탭·회원앱)
  expiringDays: strOf(setting?.expiringDays ?? 30),
  minUse: strOf(setting?.minUsePoint ?? ''),
  maxUse: strOf(setting?.maxUseValue ?? ''),
  // 1회 최대 사용 단위 — P(정액) 또는 %(구매하려는 상품 결제금액 대비)
  maxUseType: setting?.maxUseType || POINT_EARN_TYPE.FIXED,
  // 미설정(null)은 허용 — 컬럼 추가 전부터 포인트를 쓰던 지점이 갑자기 막히지 않게
  appUse: setting?.appUseYn !== 'N',
  groups: (setting?.earnGroups || []).length
    ? (setting?.earnGroups || []).map(group => ({
        name: group.name,
        on: group.enabledYn !== 'N',
        rate: strOf(group.earnValue ?? ''),
        unit: group.earnType || POINT_EARN_TYPE.PERCENT,
        methods: group.methods || [],
      }))
    : // 그룹이 없을 때의 기본 상태 — 꺼진 '새 그룹' 한 줄을 보여준다 (저장 시 미사용이면 제외)
      [{ name: '새 그룹', on: false, rate: '', unit: POINT_EARN_TYPE.PERCENT, methods: [] }],
  memberGroupPolicies: (setting?.memberGroupPolicies || []).map(policy => ({
    memberGroupId: policy.memberGroupId,
    earns: Object.fromEntries(
      PAYMENT_METHODS.map(method => {
        const earn = (policy.earns || []).find(item => item.method === method.value);
        return [method.value, { rate: strOf(earn?.earnValue), unit: earn?.earnType || POINT_EARN_TYPE.PERCENT }];
      }),
    ),
    attend: strOf(policy.attendancePoint),
    minUse: strOf(policy.minUsePoint),
    maxUse: strOf(policy.maxUseValue),
    maxUseType: policy.maxUseType || POINT_EARN_TYPE.FIXED,
    appUse: policy.appUseYn,
  })),
});

// 편집 상태 → PUT payload (전체 치환 계약)
const toPayload = state => ({
  enabledYn: state.on ? 'Y' : 'N',
  earnType: state.baseUnit,
  earnValue: numOf(state.base),
  minUsePoint: numOf(state.minUse),
  maxUseType: state.maxUse === '' ? null : state.maxUseType,
  maxUseValue: state.maxUse === '' ? null : numOf(state.maxUse),
  appUseYn: state.appUse ? 'Y' : 'N',
  attendanceEnabledYn: state.attendOn ? 'Y' : 'N',
  attendancePoint: numOf(state.attend),
  expiringDays: numOf(state.expiringDays),
  // 전체 치환 — OFF 규칙은 백엔드가 방식·기간을 비운다
  expireRules: state.expireRules.map(rule => ({
    source: rule.source,
    enabledYn: rule.on ? 'Y' : 'N',
    mode: rule.source === POINT_EXPIRE_SOURCE.PURCHASE ? rule.mode : null,
    periodValue: rule.on && rule.value !== '' ? numOf(rule.value) : null,
    periodUnit: rule.on ? rule.unit : null,
  })),
  earnGroups: state.groups
    .filter(group => group.on || group.methods.length > 0)
    .map(group => ({
    name: group.name || '새 그룹',
    enabledYn: group.on ? 'Y' : 'N',
    earnType: group.unit,
      earnValue: numOf(group.rate),
      methods: group.methods,
    })),
  memberGroupPolicies: state.memberGroupPolicies
    .filter(policy => policy.memberGroupId)
    .map(policy => ({
      memberGroupId: policy.memberGroupId,
      earns: PAYMENT_METHODS.map(method => ({
        method: method.value,
        earnType: policy.earns[method.value]?.unit || POINT_EARN_TYPE.PERCENT,
        earnValue: numOf(policy.earns[method.value]?.rate),
      })),
      attendancePoint: numOf(policy.attend),
      minUsePoint: numOf(policy.minUse),
      maxUseType: policy.maxUseType,
      maxUseValue: numOf(policy.maxUse),
      appUseYn: policy.appUse,
    })),
});

const Toggle = ({ on, onClick, width = 40, height = 22 }) => (
  <Box
    onClick={onClick}
    sx={{
      width,
      height,
      borderRadius: '99px',
      p: '3px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      boxSizing: 'border-box',
      flex: 'none',
      background: on ? TEAL : '#D5D8DE',
      justifyContent: on ? 'flex-end' : 'flex-start',
    }}
  >
    <Box sx={{ width: height - 6, height: height - 6, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,.2)' }} />
  </Box>
);

const UnitSegment = ({ value, options, onChange, size = 40 }) => (
  <Box sx={{ display: 'flex', background: '#F2F3F6', borderRadius: '8px', p: '3px', gap: '2px', height: 38, boxSizing: 'border-box', flex: 'none' }}>
    {options.map(option => (
      <Box
        key={option.value}
        onClick={() => onChange(option.value)}
        sx={{
          // size 를 'auto' 로 주면 라벨 길이만큼(유효기간 방식처럼 긴 라벨용)
          width: size === 'auto' ? 'auto' : size,
          px: size === 'auto' ? '7px' : 0,
          whiteSpace: 'nowrap',
          borderRadius: '6px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 12,
          fontWeight: 700,
          cursor: 'pointer',
          background: value === option.value ? '#fff' : 'transparent',
          color: value === option.value ? TEAL : '#8A8F98',
        }}
      >
        {option.label}
      </Box>
    ))}
  </Box>
);

const inputSx = {
  height: 38,
  border: '1px solid #DDE1E8',
  borderRadius: '8px',
  padding: '0 12px',
  fontSize: 14,
  fontWeight: 700,
  color: '#2B2D31',
  textAlign: 'right',
  width: '100%',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
};

const cardSx = { border: '1px solid #E7EAEF', borderRadius: '10px', p: '13px 15px', display: 'flex', flexDirection: 'column', gap: '10px' };
const cardHeadSx = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 22 };
const cardTitleSx = { fontSize: 13, fontWeight: 800, color: '#2B2D31' };
const policyLabelSx = { fontSize: 11, fontWeight: 700, color: '#8A8F98', mb: '4px' };
const policyInputStyle = { width: '100%', minWidth: 0, height: 34, border: '1px solid #DDE1E8', borderRadius: 7, padding: '0 9px', fontSize: 13, fontWeight: 800, textAlign: 'right', color: TEAL, boxSizing: 'border-box', fontFamily: 'inherit' };
// 기본 화살표는 브라우저가 그려 테두리에 붙으므로, 없애고 안쪽에 직접 그린다.
const SELECT_CHEVRON = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath d='M2.5 4.5 6 8l3.5-3.5' fill='none' stroke='%238A8F98' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`;
const policySelectStyle = { width: '100%', height: 34, border: '1px solid #DDE1E8', borderRadius: 7, padding: '0 28px 0 9px', fontSize: 13, fontWeight: 700, color: '#2B2D31', backgroundColor: '#fff', backgroundImage: SELECT_CHEVRON, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 9px center', appearance: 'none', WebkitAppearance: 'none', textOverflow: 'ellipsis', boxSizing: 'border-box', fontFamily: 'inherit' };

// 그룹을 추가할 때 지점 설정값(편집 중인 값 포함)으로 채운다. 수단은 켜진 결제 수단 그룹 > 기본 적립 순이다.
// 그룹에는 빈값이 없어, 지점에 1회 최대가 없으면 같은 뜻인 100% 로 채운다.
const newMemberGroupPolicy = state => ({
  memberGroupId: null,
  earns: Object.fromEntries(
    PAYMENT_METHODS.map(method => {
      const group = state.groups.find(item => item.on && item.methods.includes(method.value));
      return [method.value, group ? { rate: group.rate, unit: group.unit } : { rate: state.base, unit: state.baseUnit }];
    }),
  ),
  attend: state.attend || '0',
  minUse: state.minUse || '0',
  maxUse: state.maxUse || '100',
  maxUseType: state.maxUse ? state.maxUseType : POINT_EARN_TYPE.PERCENT,
  appUse: state.appUse ? 'Y' : 'N',
});

const isMemberGroupPolicyFilled = policy =>
  PAYMENT_METHODS.every(method => (policy.earns[method.value]?.rate ?? '') !== '') &&
  policy.attend !== '' &&
  policy.minUse !== '' &&
  policy.maxUse !== '';

const PointSettingModal = ({ open, onClose, branchId, token, pointSetting }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [tab, setTab] = useState('earn');
  const [state, setState] = useState(() => toEditState(pointSetting));
  const [dirty, setDirty] = useState(false);

  // 모달을 열 때마다 서버 값으로 초기화
  useEffect(() => {
    if (!open) return;
    setState(toEditState(pointSetting));
    setDirty(false);
    setTab('earn');
  }, [open, pointSetting]);

  const up = updater => {
    setState(prev => ({ ...prev, ...(typeof updater === 'function' ? updater(prev) : updater) }));
    setDirty(true);
  };
  const upGroup = (index, patch) =>
    up(prev => ({ groups: prev.groups.map((group, i) => (i === index ? { ...group, ...patch } : group)) }));
  const upMemberGroupPolicy = (index, patch) =>
    up(prev => ({ memberGroupPolicies: prev.memberGroupPolicies.map((policy, i) => (i === index ? { ...policy, ...patch } : policy)) }));
  const upMemberGroupEarn = (index, method, patch) =>
    up(prev => ({
      memberGroupPolicies: prev.memberGroupPolicies.map((policy, i) =>
        i === index
          ? {
              ...policy,
              earns: {
                ...policy.earns,
                [method]: { ...policy.earns[method], ...patch },
              },
            }
          : policy,
      ),
    }));
  const upExpireRule = (index, patch) =>
    up(prev => ({ expireRules: prev.expireRules.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)) }));

  // 수단 → 배정된 그룹 인덱스 (칩 중복 배정 방지)
  const assignedTo = useMemo(() => {
    const map = {};
    state.groups.forEach((group, index) => group.methods.forEach(method => { map[method] = index; }));
    return map;
  }, [state.groups]);

  const { data: memberGroups = [] } = useQuery({
    queryKey: ['memberGroups', branchId],
    queryFn: () => getMemberGroups(token, branchId),
    enabled: open && !!branchId && !!token,
  });
  const usedMemberGroupIds = state.memberGroupPolicies.map(policy => policy.memberGroupId);
  const canAddMemberGroupPolicy = memberGroups.some(group => !usedMemberGroupIds.includes(group.id));

  const saveMutation = useMutation({
    mutationFn: data => updateBranchPointSetting(branchId, data, token),
    onSuccess: () => {
      toast.success(t('저장되었습니다'));
      queryClient.invalidateQueries({ queryKey: ['branchPointSetting', branchId] });
      setDirty(false);
      onClose();
    },
    onError: error => {
      toast.error(error?.response?.data?.message || t('저장에 실패했습니다'));
    },
  });

  const handleSave = () => {
    const memberGroupPolicies = state.memberGroupPolicies.filter(policy => policy.memberGroupId);
    if (!memberGroupPolicies.every(isMemberGroupPolicyFilled)) {
      setTab('group');
      toast.error(t('회원 그룹의 모든 값을 입력해 주세요'));
      return;
    }
    if (memberGroupPolicies.some(policy => policy.maxUseType === POINT_EARN_TYPE.PERCENT && (numOf(policy.maxUse) < 1 || numOf(policy.maxUse) > 100))) {
      setTab('group');
      toast.error(t('1회 최대 사용 비율은 1~100% 사이여야 합니다'));
      return;
    }
    if (state.on) {
      if (state.base === '') {
        setTab('earn');
        toast.error(t('포인트 기본값을 입력해 주세요'));
        return;
      }
      if (state.attendOn && state.attend === '') {
        setTab('earn');
        toast.error(t('출석 적립 포인트를 입력해 주세요'));
        return;
      }
      // 켜진 규칙은 기간 필수 — 상품 기간 방식도 기간 없는 적립(환불복구 등)은 고정 기간으로 폴백한다
      if (numOf(state.expiringDays) < 1) {
        toast.error(t('소멸 예정 기준 일수를 1 이상 입력해 주세요'));
        return;
      }
      const emptyExpireRule = state.expireRules.find(rule => rule.on && numOf(rule.value) < 1);
      if (emptyExpireRule) {
        setTab('earn');
        toast.error(`${t(POINT_EXPIRE_SOURCE_LABELS[emptyExpireRule.source])} ${t('유효기간을 1 이상 입력해 주세요')}`);
        return;
      }
      const emptyRateGroup = state.groups.find(group => group.on && group.rate === '');
      if (emptyRateGroup) {
        setTab('earn');
        toast.error(`'${emptyRateGroup.name || t('새 그룹')}' ${t('그룹의 적립률을 입력해 주세요')}`);
        return;
      }
      if (state.maxUse !== '' && state.maxUseType === POINT_EARN_TYPE.PERCENT && numOf(state.maxUse) > 100) {
        setTab('use');
        toast.error(t('1회 최대 사용 비율은 100% 이하여야 합니다'));
        return;
      }
      // 모순 설정 차단 — 정액 상한이 최소보다 작으면 포인트를 아예 쓸 수 없다 (백엔드도 거부).
      // % 상한은 결제금액에 따라 달라져 정적으로 비교할 수 없다.
      if (state.maxUse !== '' && state.maxUseType === POINT_EARN_TYPE.FIXED && numOf(state.minUse) > numOf(state.maxUse)) {
        setTab('use');
        toast.error(t('최소 사용 포인트가 1회 최대 사용 한도보다 클 수 없습니다'));
        return;
      }
    }
    saveMutation.mutate(toPayload(state));
  };

  const handleCancel = () => {
    setState(toEditState(pointSetting));
    setDirty(false);
    onClose();
  };

  const tabs = [
    { id: 'earn', Icon: PaidOutlinedIcon, label: t('적립') },
    { id: 'use', Icon: ConfirmationNumberOutlinedIcon, label: t('사용') },
    { id: 'group', Icon: GroupsOutlinedIcon, label: t('회원 그룹별') },
  ];
  const titles = {
    earn: [t('적립 설정'), t('언제, 얼마를 적립할지 정합니다')],
    use: [t('사용 설정'), t('회원이 포인트를 쓸 수 있는 조건')],
    group: [t('회원 그룹별 설정'), t('그룹마다 적립과 사용 조건을 따로 정합니다')],
  };

  const maxHint =
    state.maxUse === ''
      ? t('한도를 비워두면 잔액 내에서 제한 없이 사용할 수 있습니다.')
      : state.maxUseType === POINT_EARN_TYPE.PERCENT
        ? `${t('구매하려는 상품 결제금액의')} ${numOf(state.maxUse)}%${t('까지 사용할 수 있습니다. (예: 100,000원 상품이면')} ${Math.floor((100000 * numOf(state.maxUse)) / 100).toLocaleString()}P)`
        : `${t('결제 금액과 무관하게 1회')} ${numOf(state.maxUse).toLocaleString()}P${t('까지 사용할 수 있습니다.')}`;

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth={false} PaperProps={{ sx: { borderRadius: '16px' } }}>
      <Box sx={{ width: 820, height: 620, display: 'flex', overflow: 'hidden', fontFamily: 'inherit' }}>
        {/* 좌측 사이드바 */}
        <Box sx={{ width: 148, background: '#F8F9FB', borderRight: '1px solid #ECEEF2', p: '20px 10px', display: 'flex', flexDirection: 'column', gap: '4px', boxSizing: 'border-box', flex: 'none' }}>
          <Box sx={{ fontSize: 15, fontWeight: 800, color: '#1E1F22', p: '0 10px 14px' }}>{t('포인트 설정')}</Box>
          {tabs.map(item => (
            <Box
              key={item.id}
              onClick={() => setTab(item.id)}
              sx={{
                height: 38,
                borderRadius: '8px',
                p: '0 12px',
                display: 'flex',
                alignItems: 'center',
                gap: '9px',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: tab === item.id ? 800 : 600,
                color: tab === item.id ? TEAL_DARK : '#6E747E',
                background: tab === item.id ? '#E7F8F6' : 'transparent',
              }}
            >
              <item.Icon sx={{ fontSize: 16 }} />
              {item.label}
            </Box>
          ))}
        </Box>

        {/* 우측 본문 */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', position: 'relative' }}>
          <Box sx={{ p: '18px 24px 14px', borderBottom: '1px solid #F0F1F4', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <Box>
              <Box sx={{ fontSize: 16, fontWeight: 800, color: '#1E1F22' }}>{titles[tab][0]}</Box>
              <Box sx={{ fontSize: 12, color: '#9AA0A9', mt: '3px' }}>{titles[tab][1]}</Box>
            </Box>
            {/* 타이틀 첫 줄과 수직 높이를 맞춘다 (height 24 = 16px 타이틀 line-height) */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px', height: '24px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Box sx={{ fontSize: 14, fontWeight: 800, color: '#1E1F22', whiteSpace: 'nowrap' }}>{t('포인트 기능 사용')}</Box>
                <Toggle on={state.on} onClick={() => up(prev => ({ on: !prev.on }))} />
              </Box>
              <Box onClick={handleCancel} sx={{ width: 24, height: 24, borderRadius: '7px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8A8F98', fontSize: 16, cursor: 'pointer' }}>
                ✕
              </Box>
            </Box>
          </Box>

          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: '18px 24px' }}>
            {tab === 'earn' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: '12px' }}>
                  {/* 기본 적립 */}
                  <Box sx={cardSx}>
                    <Box sx={cardHeadSx}>
                      <Box sx={cardTitleSx}>{t('기본 적립')}</Box>
                      <Box sx={{ fontSize: 11, color: '#9AA0A9' }}>{t('상품 판매')}</Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: '8px' }}>
                      <input style={inputSx} value={state.base} onChange={e => up(prev => ({ base: sanitizeRate(e.target.value, prev.baseUnit) }))} />
                      <UnitSegment
                        value={state.baseUnit}
                        options={[{ value: 'PERCENT', label: '%' }, { value: 'FIXED', label: 'P' }]}
                        onChange={value => up(prev => ({ baseUnit: value, base: sanitizeRate(prev.base, value) }))}
                      />
                    </Box>
                  </Box>

                  {/* 출석 적립 */}
                  <Box sx={cardSx}>
                    <Box sx={cardHeadSx}>
                      <Box sx={cardTitleSx}>{t('출석 적립')}</Box>
                      <Toggle on={state.attendOn} onClick={() => up(prev => ({ attendOn: !prev.attendOn }))} width={38} height={21} />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', opacity: state.attendOn ? 1 : 0.42 }}>
                      <Box sx={{ fontSize: 12, color: '#8A8F98', whiteSpace: 'nowrap' }}>{t('1회 출석당')}</Box>
                      <input style={inputSx} value={state.attend} onChange={e => up({ attend: e.target.value.replace(/[^0-9]/g, '') })} />
                      <Box sx={{ fontSize: 13, color: '#4A4F57' }}>P</Box>
                    </Box>
                  </Box>
                </Box>

                {/* 유효기간 — 적립 유형별. 설정 이후 적립분부터 적용, 이전 적립은 무기한 유지 */}
                <Box sx={cardSx}>
                  <Box sx={cardHeadSx}>
                    <Box sx={cardTitleSx}>{t('포인트 유효기간')}</Box>
                  </Box>
                  {/* 유형당 한 줄 — 4열 그리드(토글·유형명·기준 선택·기간)로 열이 맞춰져 행끼리 정렬된다 */}
                  {state.expireRules.map((rule, index) => {
                    const isPurchase = rule.source === POINT_EXPIRE_SOURCE.PURCHASE;
                    const productMode = isPurchase && rule.mode === POINT_EXPIRE_MODE.PRODUCT;
                    return (
                      <Box key={rule.source} sx={{ display: 'grid', gridTemplateColumns: '34px 88px minmax(0,1fr) auto', alignItems: 'center', columnGap: '10px', minHeight: 32 }}>
                        <Toggle on={rule.on} onClick={() => upExpireRule(index, { on: !rule.on })} width={34} height={19} />
                        <Box sx={{ fontSize: 12, fontWeight: 700, color: '#2B2D31', whiteSpace: 'nowrap' }}>{t(POINT_EXPIRE_SOURCE_LABELS[rule.source])}</Box>
                        {/* 그리드 셀에 꽉 차면 세그먼트 배경이 오른쪽까지 늘어나므로 내용 폭만 차지하게 한다 */}
                        <Box sx={{ opacity: rule.on ? 1 : 0.42, justifySelf: 'start', width: 'fit-content' }}>
                          {isPurchase && (
                            <UnitSegment
                              value={rule.mode}
                              options={[
                                { value: POINT_EXPIRE_MODE.PRODUCT, label: t('상품 기간과 매칭') },
                                { value: POINT_EXPIRE_MODE.FIXED, label: t('직접 지정') },
                              ]}
                              onChange={value => upExpireRule(index, { mode: value })}
                              size="auto"
                            />
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '6px', opacity: rule.on ? 1 : 0.42 }}>
                          <Box sx={{ fontSize: 12, color: '#8A8F98', whiteSpace: 'nowrap' }}>
                            {productMode ? t('이용기간 없으면') : t('적립일부터')}
                          </Box>
                          <input style={{ ...inputSx, width: 56, height: 32, flex: 'none' }} value={rule.value} onChange={e => upExpireRule(index, { value: e.target.value.replace(/[^0-9]/g, '') })} />
                          <UnitSegment
                            value={rule.unit}
                            options={[
                              { value: POINT_PERIOD_UNIT.DAY, label: t('일') },
                              { value: POINT_PERIOD_UNIT.MONTH, label: t('개월') },
                            ]}
                            onChange={value => upExpireRule(index, { unit: value })}
                            size={34}
                          />
                        </Box>
                      </Box>
                    );
                  })}
                  <Box sx={{ fontSize: 11, color: '#9AA0A9', lineHeight: 1.6 }}>
                    {t('상품 이용기간 기준은 구매한 회원권·이용권의 이용 종료일까지 사용할 수 있고, 이용기간이 없는 상품(일반상품·환불 복구·앱 결제)은 입력한 기간을 씁니다. 토글을 끄면 만료되지 않으며, 설정은 이후 적립분부터 적용됩니다.')}
                  </Box>
                  {/* 소멸 예정 기준 — 유효기간 규칙과 별개로 "얼마 안 남았다"를 어디까지로 볼지 */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', pt: '10px', borderTop: '1px solid #EEF0F3' }}>
                    <Box sx={{ fontSize: 12, fontWeight: 700, color: '#2B2D31', whiteSpace: 'nowrap' }}>{t('소멸 예정 기준')}</Box>
                    <Box sx={{ fontSize: 12, color: '#8A8F98', whiteSpace: 'nowrap' }}>{t('만료일이')}</Box>
                    <input style={{ ...inputSx, width: 56, height: 32, flex: 'none' }} value={state.expiringDays} onChange={e => up({ expiringDays: e.target.value.replace(/[^0-9]/g, '') })} />
                    <Box sx={{ fontSize: 12, color: '#8A8F98', whiteSpace: 'nowrap' }}>{t('일 이내인 포인트를 소멸 예정으로 표시')}</Box>
                  </Box>
                </Box>

                {/* 결제 수단별 적립 그룹 */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '9px', minWidth: 0 }}>
                      <Box sx={{ fontSize: 13, fontWeight: 800, color: '#2B2D31', whiteSpace: 'nowrap' }}>{t('결제 수단별 적립률')}</Box>
                      <Box sx={{ fontSize: 11, color: '#9AA0A9', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {t('칩을 눌러 그룹에 담으면 같은 적립률이 적용됩니다')}
                      </Box>
                    </Box>
                    <Box
                      onClick={() => up(prev => ({ groups: [...prev.groups, { name: t('새 그룹'), on: true, rate: '1', unit: 'PERCENT', methods: [] }] }))}
                      sx={{ fontSize: 12, fontWeight: 700, color: TEAL, border: '1px solid #B7E9E2', background: '#F2FCFB', p: '7px 12px', borderRadius: '8px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                    >
                      + {t('결제 수단 묶음')}
                    </Box>
                  </Box>

                  <Box sx={{ border: '1px solid #E7EAEF', background: '#F7F8FA', borderRadius: '9px', p: '10px 13px', fontSize: 12, color: '#6E747E', lineHeight: 1.6 }}>
                    {t('그룹이')} <b style={{ color: TEAL }}>{t('켜져 있는')}</b> {t('결제 수단은 해당 적립률이')}{' '}
                    <b style={{ color: '#2B2D31' }}>{t('기본 적립보다 우선')}</b> {t('적용됩니다. 미지정인 수단은 기본 적립률이 적용됩니다.')}
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {state.groups.map((group, index) => (
                      <Box key={index} sx={{ border: '1px solid #E7EAEF', borderRadius: '10px', p: '10px 12px', display: 'flex', alignItems: 'center', gap: '10px', background: group.on ? '#fff' : '#FAFBFC' }}>
                        <Toggle on={group.on} onClick={() => upGroup(index, { on: !group.on })} width={34} height={19} />
                        <input
                          value={group.name}
                          onChange={e => upGroup(index, { name: e.target.value })}
                          style={{ width: 108, height: 34, border: '1px solid transparent', borderRadius: 7, padding: '0 9px', fontSize: 13, fontWeight: 700, color: group.on ? '#2B2D31' : '#9AA0A9', background: '#F5F6F8', boxSizing: 'border-box', fontFamily: 'inherit', flex: 'none' }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', gap: '6px', flexWrap: 'wrap', opacity: group.on ? 1 : 0.42 }}>
                          {PAYMENT_METHODS.map(method => {
                            const mine = group.methods.includes(method.value);
                            const taken = assignedTo[method.value] !== undefined && !mine;
                            const ownerName = taken ? state.groups[assignedTo[method.value]].name : '';
                            return (
                              <Box
                                key={method.value}
                                title={taken ? `'${ownerName}' ${t('그룹에 이미 포함되어 있습니다')}` : ''}
                                onClick={() => {
                                  if (taken) return;
                                  upGroup(index, {
                                    methods: mine ? group.methods.filter(v => v !== method.value) : [...group.methods, method.value],
                                  });
                                }}
                                sx={{
                                  height: 28,
                                  p: '0 11px',
                                  borderRadius: '99px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  fontSize: 12,
                                  fontWeight: 700,
                                  whiteSpace: 'nowrap',
                                  cursor: taken ? 'not-allowed' : 'pointer',
                                  border: `1px solid ${mine ? TEAL : taken ? '#EEF0F3' : '#E3E6EB'}`,
                                  background: mine ? '#E7F8F6' : taken ? '#F5F6F8' : '#fff',
                                  color: mine ? TEAL_DARK : taken ? '#C3C7CE' : '#6E747E',
                                }}
                              >
                                {mine ? '✓ ' : taken ? '· ' : '+ '}
                                {t(method.label)}
                              </Box>
                            );
                          })}
                        </Box>
                        <input
                          value={group.rate}
                          onChange={e => upGroup(index, { rate: sanitizeRate(e.target.value, group.unit) })}
                          style={{ width: 58, height: 34, border: '1px solid #DDE1E8', borderRadius: 7, padding: '0 9px', fontSize: 14, fontWeight: 800, textAlign: 'right', color: group.on ? TEAL : '#9AA0A9', opacity: group.on ? 1 : 0.42, boxSizing: 'border-box', fontFamily: 'inherit', flex: 'none' }}
                        />
                        <Box sx={{ opacity: group.on ? 1 : 0.42 }}>
                          <UnitSegment
                            value={group.unit}
                            options={[{ value: 'PERCENT', label: '%' }, { value: 'FIXED', label: 'P' }]}
                            onChange={value => upGroup(index, { unit: value, rate: sanitizeRate(group.rate, value) })}
                            size={34}
                          />
                        </Box>
                        <Box
                          onClick={() => up(prev => ({ groups: prev.groups.filter((_, i) => i !== index) }))}
                          sx={{ width: 26, height: 26, borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#B6BBC3', fontSize: 13, cursor: 'pointer', flex: 'none' }}
                        >
                          ✕
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            )}

            {tab === 'use' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                {/* 최소/최대는 현재 정액(P)만 지원 — %가 필요해지면 UnitSegment 를 되살린다 */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: '12px' }}>
                  <Box sx={cardSx}>
                    <Box sx={cardHeadSx}>
                      <Box sx={cardTitleSx}>{t('최소 사용 포인트')}</Box>
                      <Box sx={{ fontSize: 11, color: '#9AA0A9' }}>{t('이 금액부터 사용 가능')}</Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <input style={inputSx} value={state.minUse} onChange={e => up({ minUse: e.target.value.replace(/[^0-9]/g, '') })} />
                      <Box sx={{ fontSize: 13, fontWeight: 700, color: '#4A4F57' }}>P</Box>
                    </Box>
                  </Box>

                  <Box sx={cardSx}>
                    <Box sx={cardHeadSx}>
                      <Box sx={cardTitleSx}>{t('1회 최대 사용')}</Box>
                      <Box sx={{ fontSize: 11, color: '#9AA0A9' }}>{t('결제당 사용 한도')}</Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <input style={inputSx} value={state.maxUse} onChange={e => up({ maxUse: e.target.value.replace(/[^0-9]/g, '') })} />
                      <UnitSegment
                        value={state.maxUseType}
                        options={[{ value: POINT_EARN_TYPE.FIXED, label: 'P' }, { value: POINT_EARN_TYPE.PERCENT, label: '%' }]}
                        onChange={value => up({ maxUseType: value })}
                      />
                    </Box>
                  </Box>
                </Box>

                <Box sx={{ background: '#F7F8FA', border: '1px solid #EDEFF3', borderRadius: '10px', p: '12px 15px', fontSize: 12, color: '#4A4F57', lineHeight: 1.6 }}>
                  {maxHint}
                </Box>

                {/* 회원앱 경로만 막는 스위치 — CRM 수기 사용은 이 값과 무관하게 계속 가능하다 */}
                <Box sx={cardSx}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Toggle on={state.appUse} onClick={() => up({ appUse: !state.appUse })} width={34} height={19} />
                    <Box>
                      <Box sx={cardTitleSx}>{t('앱 포인트 사용 허용')}</Box>
                      <Box sx={{ fontSize: 11, color: '#9AA0A9', mt: '2px' }}>
                        {state.appUse
                          ? t('회원이 앱 결제에서 포인트를 사용할 수 있습니다')
                          : t('회원 앱에서는 사용할 수 없고, CRM 수기 판매등록에서는 계속 사용할 수 있습니다')}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}
            {tab === 'group' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                  <Box sx={{ fontSize: 11, color: '#9AA0A9' }}>
                    {t('새 그룹은 지점 설정값으로 채워집니다. 여러 그룹에 속하면 회원에게 유리한 값을 적용합니다')}
                  </Box>
                  <Box
                    onClick={() => {
                      if (!canAddMemberGroupPolicy) return;
                      up(prev => ({ memberGroupPolicies: [...prev.memberGroupPolicies, newMemberGroupPolicy(prev)] }));
                    }}
                    sx={{ fontSize: 12, fontWeight: 700, color: canAddMemberGroupPolicy ? TEAL : '#C3C7CE', border: `1px solid ${canAddMemberGroupPolicy ? '#B7E9E2' : '#EEF0F3'}`, background: canAddMemberGroupPolicy ? '#F2FCFB' : '#F5F6F8', borderRadius: '7px', p: '5px 11px', cursor: canAddMemberGroupPolicy ? 'pointer' : 'default', whiteSpace: 'nowrap', flex: 'none' }}
                  >
                    + {t('회원 그룹')}
                  </Box>
                </Box>

                {memberGroups.length === 0 && (
                  <Box sx={{ border: '1px solid #E7EAEF', background: '#F7F8FA', borderRadius: '9px', p: '10px 13px', fontSize: 12, color: '#6E747E', lineHeight: 1.6 }}>
                    {t('만들어 둔 회원 그룹이 없습니다. 회원 관리의 그룹 관리에서 먼저 만들어 주세요.')}
                  </Box>
                )}

                {state.memberGroupPolicies.map((policy, index) => (
                  <Box key={index} sx={cardSx}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <select
                        value={policy.memberGroupId ?? ''}
                        onChange={e => upMemberGroupPolicy(index, { memberGroupId: e.target.value === '' ? null : Number(e.target.value) })}
                        style={{ ...policySelectStyle, flex: 1, minWidth: 0 }}
                      >
                        <option value="">{t('회원 그룹 선택')}</option>
                        {memberGroups
                          .filter(group => group.id === policy.memberGroupId || !usedMemberGroupIds.includes(group.id))
                          .map(group => (
                            <option key={group.id} value={group.id}>
                              {group.name} ({group.memberCount}{t('명')})
                            </option>
                          ))}
                      </select>
                      <Box
                        onClick={() => up(prev => ({ memberGroupPolicies: prev.memberGroupPolicies.filter((_, i) => i !== index) }))}
                        sx={{ width: 26, height: 26, borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#B4B9C2', fontSize: 13, cursor: 'pointer', flex: 'none' }}
                      >
                        ✕
                      </Box>
                    </Box>

                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: '10px' }}>
                      {PAYMENT_METHODS.map(method => {
                        const earn = policy.earns[method.value];
                        return (
                          <Box key={method.value}>
                            <Box sx={policyLabelSx}>{t(method.label)} {t('적립')}</Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <input
                                value={earn.rate}
                                onChange={e => upMemberGroupEarn(index, method.value, { rate: sanitizeRate(e.target.value, earn.unit) })}
                                style={policyInputStyle}
                              />
                              <UnitSegment
                                value={earn.unit}
                                options={[{ value: 'PERCENT', label: '%' }, { value: 'FIXED', label: 'P' }]}
                                onChange={value => upMemberGroupEarn(index, method.value, { unit: value, rate: sanitizeRate(earn.rate, value) })}
                                size={26}
                              />
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>

                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: '10px' }}>
                      <Box>
                        <Box sx={policyLabelSx}>{t('출석 적립')}</Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <input value={policy.attend} onChange={e => upMemberGroupPolicy(index, { attend: e.target.value.replace(/[^0-9]/g, '') })} style={policyInputStyle} />
                          <Box sx={{ fontSize: 12, fontWeight: 700, color: '#4A4F57' }}>P</Box>
                        </Box>
                      </Box>
                      <Box>
                        <Box sx={policyLabelSx}>{t('최소 사용')}</Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <input value={policy.minUse} onChange={e => upMemberGroupPolicy(index, { minUse: e.target.value.replace(/[^0-9]/g, '') })} style={policyInputStyle} />
                          <Box sx={{ fontSize: 12, fontWeight: 700, color: '#4A4F57' }}>P</Box>
                        </Box>
                      </Box>
                      <Box>
                        <Box sx={policyLabelSx}>{t('1회 최대 사용')}</Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <input value={policy.maxUse} onChange={e => upMemberGroupPolicy(index, { maxUse: e.target.value.replace(/[^0-9]/g, '') })} style={policyInputStyle} />
                          <UnitSegment
                            value={policy.maxUseType}
                            options={[{ value: POINT_EARN_TYPE.FIXED, label: 'P' }, { value: POINT_EARN_TYPE.PERCENT, label: '%' }]}
                            onChange={value => upMemberGroupPolicy(index, { maxUseType: value })}
                            size={26}
                          />
                        </Box>
                      </Box>
                      <Box>
                        <Box sx={policyLabelSx}>{t('앱 포인트 사용')}</Box>
                        <select
                          value={policy.appUse}
                          onChange={e => upMemberGroupPolicy(index, { appUse: e.target.value })}
                          style={policySelectStyle}
                        >
                          <option value="Y">{t('허용')}</option>
                          <option value="N">{t('차단')}</option>
                        </select>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          {/* 기능 꺼짐 오버레이 */}
          {!state.on && (
            <Box sx={{ position: 'absolute', top: 64, left: 0, right: 0, bottom: 64, background: 'rgba(255,255,255,.72)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {/* 본문은 사이드바(148px)를 제외한 영역이라, 카드만 절반만큼 왼쪽으로 밀어 모달 전체 기준 중앙에 보이게 한다 */}
              <Box
                sx={{
                  transform: 'translateX(-74px)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '10px',
                  background: '#fff',
                  border: '1px solid #ECEEF2',
                  borderRadius: '14px',
                  p: '22px 34px',
                  boxShadow: '0 8px 28px rgba(30,31,34,.10)',
                }}
              >
                <Box sx={{ width: 40, height: 40, borderRadius: '50%', background: '#E7F8F6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ToggleOffOutlinedIcon sx={{ fontSize: 24, color: TEAL_DARK }} />
                </Box>
                <Box sx={{ fontSize: 14, fontWeight: 800, color: '#1E1F22' }}>{t('포인트 기능이 꺼져 있습니다')}</Box>
                <Box sx={{ fontSize: 12, color: '#9AA0A9' }}>{t('우측 상단의 포인트 기능 사용을 켜 주세요')}</Box>
              </Box>
            </Box>
          )}

          {/* 하단 저장 바 */}
          <Box sx={{ borderTop: '1px solid #F0F1F4', p: '13px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: dirty ? '#FFFBFA' : '#fff' }}>
            <Box sx={{ fontSize: 12, fontWeight: 600, color: dirty ? '#F1685A' : '#9AA0A9' }}>
              {dirty ? t('저장하지 않은 변경사항이 있습니다') : t('모든 변경사항이 저장되었습니다')}
            </Box>
            <Box sx={{ display: 'flex', gap: '8px' }}>
              <Box onClick={handleCancel} sx={{ height: 38, boxSizing: 'border-box', p: '0 16px', borderRadius: '8px', border: '1px solid #DDE1E8', display: 'flex', alignItems: 'center', fontSize: 13, fontWeight: 700, color: '#4A4F57', cursor: 'pointer', background: '#fff' }}>
                {t('취소')}
              </Box>
              <Box
                onClick={() => !saveMutation.isPending && dirty && handleSave()}
                sx={{ height: 38, boxSizing: 'border-box', p: '0 22px', borderRadius: '8px', background: dirty ? TEAL : '#C9CED6', display: 'flex', alignItems: 'center', fontSize: 13, fontWeight: 700, color: '#fff', cursor: dirty ? 'pointer' : 'default' }}
              >
                {t('저장')}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default PointSettingModal;
